import _ from 'lodash';
import PropTypes, { InferProps } from 'prop-types';
//MUI
import { ListItemIcon, ListItem, ListItemButton, ListItemText, useTheme, Box, Dialog, Button, Typography, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
//Router
import { Link, useLocation, useNavigate } from 'react-router-dom';
//Hooks
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations, selectLanguage, selectImageUrl } from 'state/reducers/translation.reducers';
//Constants
import { DashboardPages, noBalancePages, pagesEnum, pageType } from 'constants/ui.constants';
//styles
import { Drawer, CustomList, DrawerHeader, IconButton, sxSelected, Version } from 'theme/layouts/sidebar';
import { getMobile, togglePage } from 'state/reducers/ui.reducers';
import { getCompanies, getUserDetails, getUserPermission } from 'state/reducers/user.reducers';
import { Fragment, useMemo, useState } from 'react';
import { ProfileAvatar } from 'theme/layouts/navbar';
import { LightGreenChip } from 'theme/elements/Chips';
import { Stack } from '@mui/system';
import { Paragraph6 } from 'theme/elements/Paragraphs';
import { DialogCard } from 'theme/elements/Cards';
import Profile from './Profile';
import { UserPermission } from 'constants/user.constants';
import { getCompanyBalances } from 'state/reducers/balance.reducers';

DashboardSidebar.propTypes = {
  open: PropTypes.bool.isRequired,
  toggleSidebar: PropTypes.func.isRequired,
  mobile: PropTypes.bool,
  setAnchorEl: PropTypes.func.isRequired,
  anchorEl: PropTypes.any,
};

export default function DashboardSidebar({
  anchorEl,
  setAnchorEl,
  toggleSidebar,
  open,
  mobile,
}: InferProps<typeof DashboardSidebar.propTypes>) {
  const location = useLocation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const t = useAppSelector(selectTranslations);
  const imageUrl = useAppSelector(selectImageUrl);
  const lang = useAppSelector(selectLanguage);
  const isRTL = _.includes(lang, 'he');
  const dir = isRTL ? 'right' : 'left';
  const userPermission = useAppSelector(getUserPermission);
  const user = useAppSelector(getUserDetails);
  const [profileDialog, setProfileDialog] = useState(false);
  const companyBalances = useAppSelector(getCompanyBalances);
  const isMobile = useAppSelector(getMobile);

  const ifToDisplay = (permission: UserPermission, page: pageType) => {
    let flag = _.includes(page.userPermission, userPermission);
    if (!companyBalances.length) {
      flag = !_.includes(noBalancePages, page.name) ? false : flag;
    }
    return flag;
  };
  const companies = useAppSelector(getCompanies);

  return (
    <Drawer anchor={dir} variant="permanent" open={open}>
      <DrawerHeader open={open}>
        <IconButton onClick={toggleSidebar} sx={{ display: { sm: 'block', xs: 'none' } }}>
          <img src={imageUrl('navbar.menu_button.icon') || '/icons/menu.svg'} />
        </IconButton>
        <IconButton onClick={toggleSidebar} sx={{ display: { xs: 'block', sm: 'none' } }}>
          <CloseIcon />
        </IconButton>
        <IconButton
          className="desktop-item"
          id="basic-button"
          sx={{ display: { xs: 'block', sm: 'none' } }}
          aria-haspopup="true"
        //aria-expanded={openMenu ? 'true' : undefined}
        //</DrawerHeader>onClick={handleClick}
        >
          <LightGreenChip>
            <Stack direction={'row'} spacing={2}>
              <span onClick={() => setProfileDialog(true)}>
                <Paragraph6
                  text={user?.companyAccountId}
                  title={`${user?.companyName}`}
                  sx={{ cursor: 'pointer' }}
                />
              </span>
              <ProfileAvatar>
                {_.has(user, 'companyName') ? _.upperCase(user.companyName)[0] : ''}
              </ProfileAvatar>
            </Stack>
          </LightGreenChip>
        </IconButton>
      </DrawerHeader>
      <CustomList>
        {DashboardPages.map((page, index) => (
          <Fragment key={index}>
            {ifToDisplay(userPermission, page) && (
              <ListItem disablePadding>
                <Box sx={sxSelected(theme, _.includes(location.pathname, page.name), open)}>
                  <ListItemButton
                    onClick={() => {
                      if (mobile) {
                        setTimeout(() => toggleSidebar(), 300);
                      }
                      dispatch(togglePage(page.link));
                    }}
                    sx={{
                      minHeight: 48,
                      [theme.breakpoints.down('sm')]: { minHeight: 38 },
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                      '&:hover': { backgroundColor: 'inherit!important' },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 2 : 'auto',
                        // ml: open ? 2 : 'auto',
                        justifyContent: 'center',
                      }}
                    >
                      {page.icon ? (
                        <img
                          src={imageUrl('navbar.' + page.name + '_button.icon') || page.icon}
                        />
                      ) : (
                        ''
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={t('navbar.' + page.name + '_button.text')}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </Box>
              </ListItem>
            )}
          </Fragment>
        ))}
      </CustomList>
      <Box sx={{ display: { xs: 'none', sm: 'block' }, flexGrow: 1 }}></Box>
      <Stack sx={{ gap: 1, padding: '16px 24px', display: !open ? 'none' : 'flex' }} className={'links-to-terms'}>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'center', sm: 'flex-start' }, padding: '0px 0' }}>
          <Link
            to='https://www.as-invest.co.il/about/%D7%9E%D7%93%D7%99%D7%A0%D7%99%D7%95%D7%AA-%D7%94%D7%A4%D7%A8%D7%98%D7%99%D7%95%D7%AA-%D7%91%D7%90%D7%AA%D7%A8%D7%99-%D7%90%D7%9C%D7%98%D7%A9%D7%95%D7%9C%D7%A8-%D7%A9%D7%97%D7%9D/'
            target="_blank"
            onClick={() => { }}
            style={{ textDecoration: 'none', fontSize: '11px', color: 'inherit' }}
          >
            {t('navbar.privacy.text')}
          </Link>
          <Link
            to={'/' + pagesEnum.TERMS}
            style={{
              marginLeft: isMobile ? '16px' : '0px',
              textDecoration: 'none',
              fontSize: '11px',
              color: 'inherit',
            }}
          >
            {t('navbar.terms.text')}
          </Link>
        </Box>
        <Divider sx={{ borderColor: 'rgba(64, 64, 64, .4)' }} />
        <Typography sx={{ textWrap: 'wrap', color: 'rgba(64, 64, 64, .6)', lineHeight: 1.2 }} variant='semiCaptionRegular'>{t('navbar.license.text')}</Typography>
      </Stack>
      <DialogCard style={{ width: '100%' }} open={profileDialog} handleClose={() => setProfileDialog(false)}>
        <Profile handleClose={() => setProfileDialog(false)} companies={companies} />
      </DialogCard>
    </Drawer>
  );
}
